import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from '@emotion/styled'
import { useTheme } from '../../contexts/themeContext'
import { ReactComponent as Globe } from '../../assets/svgs/globe.svg'
import { ReactComponent as ChevronDown } from '../../assets/svgs/chevronDown.svg'
import FlagEN from '../../assets/svgs/FlagEN'
import FlagAR from '../../assets/svgs/FlagAR'
import FlagDE from '../../assets/svgs/FlagDE'

const locales = {
  en: { title: 'English', Flag: FlagEN },
  ar: { title: 'العربية', Flag: FlagAR },
  gr: { title: 'Deutsch', Flag: FlagDE },
}

const Wrapper = styled('div')({
  position: 'relative',
  display: 'inline-block',
})

const ToggleBtn = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 12px',
  border: `1.5px solid ${theme.tapBackgroundActive}`,
  borderRadius: '20px',
  backgroundColor: 'transparent',
  color: theme.brandColorOrange,
  fontWeight: 600,
  fontSize: '0.875rem',
  cursor: 'pointer',
  outline: 'none',
  '&:focus': {
    outline: 'none',
    boxShadow: 'none',
  },
}))

const Menu = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 'calc(100% + 6px)',
  insetInlineEnd: 0,
  minWidth: '160px',
  backgroundColor: theme.white,
  borderRadius: '10px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
  zIndex: 9999,
  overflow: 'hidden',
  padding: '4px 0',
}))

const MenuItem = styled('button')(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  width: '100%',
  padding: '10px 16px',
  border: 'none',
  backgroundColor: active ? theme.water : 'transparent',
  color: theme.brandColorOrange,
  fontWeight: active ? 600 : 400,
  fontSize: '0.875rem',
  cursor: 'pointer',
  textAlign: 'start',
  '&:hover': {
    backgroundColor: theme.water,
  },
}))

export default function LanguagesDropdown() {
  const { i18n } = useTranslation()
  const { theme } = useTheme()
  const [menu, setMenu] = React.useState(false)
  const ref = React.useRef(null)

  const currentLocale = locales[i18n.language] || locales.en

  useEffect(() => {
    const dir = i18n.dir(i18n.language)
    document.documentElement.dir = dir
  }, [i18n, i18n.language])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <Wrapper ref={ref}>
      <ToggleBtn type="button" onClick={() => setMenu((v) => !v)}>
        <Globe
          width="16"
          height="16"
          style={{ color: theme.brandColorOrange }}
        />
        {currentLocale.title}
        <ChevronDown
          width="12"
          height="12"
          style={{
            color: theme.brandColorOrange,
            transform: menu ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        />
      </ToggleBtn>

      {menu && (
        <Menu>
          {Object.keys(locales).map((locale) => {
            const { title, Flag } = locales[locale]
            return (
              <MenuItem
                key={locale}
                active={i18n.language === locale}
                type="button"
                onClick={() => {
                  i18n.changeLanguage(locale)
                  setMenu(false)
                }}
              >
                <Flag width="22" height="16" />
                {title}
              </MenuItem>
            )
          })}
        </Menu>
      )}
    </Wrapper>
  )
}
